import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import { FaUndo } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';

import { listItemMsg } from '@/shared/components/FormattedListValue';

import Toggle from 'react-toggle';
import MultiSelect from '@/shared/components/input/multiselect/MultiSelect';
import enumCache from '@/shared/services/EnumCacheService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import messages from './notificationMessages';

export const defaultFilters = {
  createDateFrom: moment.utc().startOf('month'),
  createDateTo: moment.utc().endOf('month'),
  period: 'month',
  notificationTypes: [],
  includeExpired: true,
  sharedWithMe: false,
};

class NotificationFilters extends Component {
  state = {
    notificationTypes: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      createDateFrom: momentPropTypes.momentObj,
      createDateTo: momentPropTypes.momentObj,
      period: PropTypes.oneOf(['all', 'month', 'quarter', 'year']),
      sharedWithMe: PropTypes.bool,
    }),
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    filters: {
      createDateFrom: null,
      createDateTo: null,
      period: null,
      sharedWithMe: false,
    },
  };

  componentDidMount() {
    const { filters, intl } = this.props;

    enumCache.get('notificationType').then(data => {
      this.setState({
        notificationTypes: data
          .map(x => ({
            id: x.key,
            label: intl.formatMessage(listItemMsg('notificationTypeLabel', x.key, x.value)),
          }))
          .filter(x => x.id !== 400 && x.id !== 500) // filter out 'tenant expiring' and 'custom' notification types
          .sort(dynamicSort('label')),
      });
    });

    if (filters && filters.period) {
      this.setPeriod(filters.period);
    }
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    onFilterChange(newFilters);
  };

  createDateFromChanged = value => {
    this.filterChanged({ createDateFrom: value });
  };
  createDateToChanged = value => {
    this.filterChanged({ createDateTo: value });
  };

  createDateChanged = (startDate, endDate) => {
    this.filterChanged({ createDateFrom: startDate, createDateTo: endDate, period: null });
  };
  toggleInactive = active => {
    this.filterChanged({ includeExpired: !!active });
  };
  resetFilters = () => {
    this.filterChanged(defaultFilters);
  };

  setPeriod = period => {
    let createDateFrom = null;
    let createDateTo = null;
    switch (period) {
      case 'all':
      case '':
      case null:
      case undefined:
        break;
      case 'day':
      case 'week':
      case 'month':
        createDateFrom = moment.utc().startOf(period);
        createDateTo = moment.utc().endOf(period);
        break;
      default:
        console.debug(`Unknown period: ${period}`);
        return;
    }

    this.filterChanged({ createDateFrom, createDateTo, period });
  };

  onNotificationTypeFilterAdded = selectedItem => {
    if (!selectedItem) return;

    const { filters } = this.props;
    const { notificationTypes } = filters;
    if (notificationTypes.includes(selectedItem.id)) return;
    this.filterChanged({
      notificationTypes: [...notificationTypes, selectedItem.id],
    });
  };

  onNotificationTypeFilterRemoved = item => {
    const { filters } = this.props;
    const { notificationTypes } = filters;
    const index = notificationTypes.findIndex(x => x === item.value.id);
    const copy = [...notificationTypes];
    copy.splice(index, 1);

    this.filterChanged({ notificationTypes: copy });
  };

  render() {
    const { notificationTypes } = this.state;
    const { filters, intl, total } = this.props;
    const defaultBtnClass = 'btn btn-default';

    const hasFilters = JSON.stringify(filters) !== JSON.stringify(defaultFilters);

    // transform filter (array of task status integers to list of object with label and id)
    const selectedNotificationTypes = notificationTypes
      ? notificationTypes.filter(r => filters.notificationTypes.includes(r.id))
      : [];

    return (
      <div className="info-block container-fluid radius4B">
        <div className="row">
          <div className="col-md-3">
            <MultiSelect
              selectedItem={selectedNotificationTypes}
              onItemRemoved={this.onNotificationTypeFilterRemoved}
              onItemSelected={this.onNotificationTypeFilterAdded}
              items={notificationTypes}
              placeholder={intl.formatMessage(messages.NotificationType)}
            />
          </div>

          <div className="col-md-6 margin5B">
            <DelayedDateRangePicker
              startDate={filters.createDateFrom}
              startDateId="NotificationFiltersCreateDateFrom"
              endDate={filters.createDateTo}
              endDateId="NotificationFiltersCreateDateTo"
              onDatesChange={({ startDate, endDate }) => this.createDateChanged(startDate, endDate)}
              startDatePlaceholderText={intl.formatMessage(messages.CreatedOn)}
              endDatePlaceholderText={intl.formatMessage(messages.CreatedOn)}
            />
            <div role="group" aria-label="period" className="btn-group margin5L margin10R">
              <button
                onClick={() => this.setPeriod('day')}
                type="button"
                className={classnames(defaultBtnClass, { active: filters.period === 'day' })}
              >
                <FormattedMessage {...messages.Today} />
              </button>
              <button
                onClick={() => this.setPeriod('week')}
                type="button"
                className={classnames(defaultBtnClass, { active: filters.period === 'week' })}
              >
                <FormattedMessage {...messages.ThisWeek} />
              </button>
              <button
                onClick={() => this.setPeriod('month')}
                type="button"
                className={classnames(defaultBtnClass, { active: filters.period === 'month' })}
              >
                <FormattedMessage {...messages.ThisMonth} />
              </button>
              <button
                onClick={() => this.setPeriod('all')}
                type="button"
                className={classnames(defaultBtnClass, {
                  active: !filters.createDateFrom && !filters.createDateTo,
                })}
              >
                <FormattedMessage {...messages.All} />
              </button>
            </div>
          </div>
          <div className="col-md-3 margin5B">
            <label className="react-toggle-wrapper margin5T">
              <Toggle
                checked={filters.sharedWithMe}
                onChange={e => this.filterChanged({ sharedWithMe: e.target.checked })}
              />
              <FormattedMessage className="label-text" {...messages.SharedWithMe} />
            </label>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-10 col-sm-10 margin5B">
            <label className="react-toggle-wrapper margin5T">
              <Toggle
                checked={filters.includeExpired}
                onChange={e => this.toggleInactive(e.target.checked)}
              />
              <FormattedMessage className="label-text" {...messages.ShowHistory} />
            </label>

            {hasFilters && (
              <button
                type="button"
                className="btn btn-default btn-reset margin10L"
                onClick={() => this.resetFilters()}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            )}
          </div>
          <div className="col-md-2  col-sm-2 margin-10B content-right pull-bottom">
            <span>
              {total}{' '}
              {total > 1 ? (
                <FormattedMessage className="label-text" {...messages.Notifications} />
              ) : (
                <FormattedMessage className="label-text" {...messages.Notification} />
              )}
            </span>
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(NotificationFilters);
